import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, DatePicker, Empty, Modal, Table, Tag } from "antd";
import dayjs from "dayjs";
import axios from "utils/axios";
import configFile from "config.json";

const { RangePicker } = DatePicker;
const DATE_FORMAT = "YYYY-MM-DD";
const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;
const DEFAULT_PAGE_SIZE = 20;

const getEffectiveRange = (range) => ({
  from: range?.[0] || dayjs(),
  to: range?.[1] || dayjs(),
});

const buildRequestPayload = (range, paging) => {
  const { from, to } = getEffectiveRange(range);
  return {
    dateFrom: from.startOf("day").toISOString(),
    dateTo: to.endOf("day").toISOString(),
    limit: paging.pageSize,
    offset: (paging.current - 1) * paging.pageSize,
  };
};

const normalizeJsonb = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
  return value;
};

const stringifyJsonb = (value) => {
  const normalized = normalizeJsonb(value);
  if (normalized === null) {
    return "-";
  }
  if (typeof normalized === "string") {
    return normalized;
  }
  try {
    return JSON.stringify(normalized, null, 2);
  } catch (error) {
    return String(normalized);
  }
};

const formatChangedFields = (value) => {
  const normalized = normalizeJsonb(value);
  if (!normalized) {
    return "-";
  }
  if (Array.isArray(normalized)) {
    return normalized.join(", ");
  }
  if (typeof normalized === "object") {
    return Object.keys(normalized).join(", ");
  }
  return String(normalized);
};

const formatDateTime = (value) =>
  value ? dayjs(value).format(DATETIME_FORMAT) : "-";

const getActionTagColor = (action) => {
  switch (action) {
    case "CREATE":
      return "green";
    case "UPDATE":
      return "blue";
    case "DELETE":
      return "red";
    default:
      return "default";
  }
};

const AuditLogs = () => {
  const [dateRange, setDateRange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;

  const selectedRangeLabel = useMemo(() => {
    const { from, to } = getEffectiveRange(dateRange);
    return `${from.format(DATE_FORMAT)} - ${to.format(DATE_FORMAT)}`;
  }, [dateRange]);

  const columns = useMemo(
    () => [
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 190,
        render: (value) => formatDateTime(value),
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        width: 110,
        render: (value) => <Tag color={getActionTagColor(value)}>{value || "-"}</Tag>,
      },
      {
        title: "Model",
        dataIndex: "modelName",
        key: "modelName",
        width: 200,
        ellipsis: true,
      },
      {
        title: "Record ID",
        dataIndex: "recordId",
        key: "recordId",
        width: 280,
        ellipsis: true,
      },
      {
        title: "Updated By",
        dataIndex: "updatedBy",
        key: "updatedBy",
        width: 260,
        ellipsis: true,
      },
      {
        title: "Changed Fields",
        dataIndex: "changedFields",
        key: "changedFields",
        width: 260,
        ellipsis: true,
        render: (value) => formatChangedFields(value),
      },
      {
        title: "Details",
        key: "details",
        fixed: "right",
        width: 110,
        render: (_, record) => (
          <Button size="small" onClick={() => setSelectedLog(record)}>
            View
          </Button>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    let isMounted = true;

    const fetchAuditLogs = async () => {
      setIsLoading(true);
      try {
        const payload = buildRequestPayload(dateRange, {
          current: currentPage,
          pageSize,
        });
        const { data: result } = await axios.post(
          `${API_BASE}/auditLog/getAuditLogs`,
          payload
        );
        if (isMounted) {
          const nextRows = Array.isArray(result?.data?.rows)
            ? result.data.rows
            : [];
          const nextTotal =
            typeof result?.data?.total === "number"
              ? result.data.total
              : nextRows.length;
          setPagination((prev) => ({ ...prev, total: nextTotal }));
          setLogs(nextRows);
        }
      } catch (error) {
        if (isMounted) {
          setPagination((prev) => ({ ...prev, total: 0 }));
          setLogs([]);
        }
        console.log(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAuditLogs();
    return () => {
      isMounted = false;
    };
  }, [dateRange, currentPage, pageSize]);

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (nextPagination) => {
    const nextPageSize = nextPagination?.pageSize || DEFAULT_PAGE_SIZE;
    const pageSizeChanged = nextPageSize !== pagination.pageSize;
    setPagination((prev) => ({
      ...prev,
      current: pageSizeChanged ? 1 : nextPagination?.current || 1,
      pageSize: nextPageSize,
    }));
  };

  return (
    <PageShell>
      <HeaderRow>
        <TitleBlock>
          <h1>Audit Logs</h1>
          <p>Бүтээгдэхүүнтэй холбоотой өөрчлөлтийн түүх.</p>
        </TitleBlock>
        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format={DATE_FORMAT}
          allowClear
        />
      </HeaderRow>

      <FilterInfo>Сонгосон огноо: {selectedRangeLabel}</FilterInfo>

      <ContentCard>
        <Table
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={logs}
          loading={isLoading}
          scroll={{ x: 1350 }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: <Empty description="Audit logs хараахан байхгүй байна." />,
          }}
        />
      </ContentCard>

      <Modal
        title="Audit Log Details"
        open={Boolean(selectedLog)}
        onCancel={() => setSelectedLog(null)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        {selectedLog && (
          <DetailsLayout>
            <DetailItem>
              <DetailLabel>ID</DetailLabel>
              <DetailValue>{selectedLog.id || "-"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Model</DetailLabel>
              <DetailValue>{selectedLog.modelName || "-"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Record ID</DetailLabel>
              <DetailValue>{selectedLog.recordId || "-"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Action</DetailLabel>
              <DetailValue>{selectedLog.action || "-"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Updated By</DetailLabel>
              <DetailValue>{selectedLog.updatedBy || "-"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Created At</DetailLabel>
              <DetailValue>{formatDateTime(selectedLog.createdAt)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Updated At</DetailLabel>
              <DetailValue>{formatDateTime(selectedLog.updatedAt)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Changed Fields</DetailLabel>
              <DetailValue>{formatChangedFields(selectedLog.changedFields)}</DetailValue>
            </DetailItem>

            <JsonGroup>
              <JsonTitle>Old Values (JSONB)</JsonTitle>
              <JsonBlock>{stringifyJsonb(selectedLog.oldValues)}</JsonBlock>
            </JsonGroup>
            <JsonGroup>
              <JsonTitle>New Values (JSONB)</JsonTitle>
              <JsonBlock>{stringifyJsonb(selectedLog.newValues)}</JsonBlock>
            </JsonGroup>
          </DetailsLayout>
        )}
      </Modal>
    </PageShell>
  );
};

const PageShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  h1 {
    margin: 0;
    font-size: 24px;
  }

  p {
    margin: 6px 0 0;
    color: rgba(0, 0, 0, 0.6);
  }
`;

const FilterInfo = styled.div`
  color: rgba(0, 0, 0, 0.65);
`;

const ContentCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 12px;
`;

const DetailsLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  color: rgba(0, 0, 0, 0.85);
  word-break: break-word;
`;

const JsonGroup = styled.div`
  grid-column: 1 / -1;
`;

const JsonTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const JsonBlock = styled.pre`
  margin: 0;
  padding: 12px;
  background: #f7f8fa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: auto;
  max-height: 280px;
  white-space: pre-wrap;
  word-break: break-word;
`;

export default AuditLogs;
